import { useMemo, useState } from 'react';

import { IContentTypeValue } from 'graphql/types';
import { IFilterFormItem, TFilterFormValues } from 'common/types';
import { useExtraCategoryFilters } from 'common/hooks/useExtraCategoryFilters';
import { useUrlFilterParams } from 'common/hooks/useUrlFilterParams';
import {
    filtersToBase64String,
    filtersToQueryParams,
} from 'common/utils/filter';
import { IUserFilterValues } from 'user/types';
import { userGroupsFilter } from 'common/utils/drawerFilters';
import { skillComplianceFilter } from 'skill/constants/filter';

export const useSkillUserFilter = () => {
    const [searchQueryParam, setSearchQueryParam] = useState('');
    const { filterParams, setFilterParam } = useUrlFilterParams('user');

    const { filters: extraCategoryFilters, loading } = useExtraCategoryFilters(
        IContentTypeValue.User
    );

    const filters = useMemo(
        () =>
            [
                userGroupsFilter(
                    'skillReportMatrixFilters.userGroups',
                    'groupIds'
                ),
                skillComplianceFilter(),
                ...extraCategoryFilters,
            ] as IFilterFormItem[],
        [extraCategoryFilters]
    );

    const selectedFilterParams = filterParams.filters;

    const handleSearch = (searchValue: string) => {
        setSearchQueryParam(searchValue);
    };

    const handleFilterChange = (values?: TFilterFormValues) => {
        const filterString = filtersToBase64String(filters, values);

        setFilterParam('filters', filterString);
    };

    const selectedFilters = useMemo(() => {
        if (!selectedFilterParams) return {};

        try {
            return JSON.parse(window.atob(selectedFilterParams));
        } catch {
            return {};
        }
    }, [selectedFilterParams]);

    const filterValuesForQuery = useMemo(
        () => filtersToQueryParams<IUserFilterValues>(filters, selectedFilters),
        [filters, selectedFilters]
    );

    const { isCompliant } = filterValuesForQuery as Partial<IUserFilterValues> &
        Record<string, unknown>;

    return {
        filters,
        filterValues: { ...selectedFilters, q: searchQueryParam },
        filterValuesForQuery: {
            ...filterValuesForQuery,
            q: searchQueryParam,
        },
        isCompliant,
        loading,
        onSearch: handleSearch,
        onFilterChange: handleFilterChange,
    };
};
